import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiBarChart2, 
  FiFileText, 
  FiDollarSign, 
  FiMail, 
  FiLogOut, 
  FiMenu, 
  FiX,
  FiShield,
  FiExternalLink,
  FiUser,
  FiImage
} from 'react-icons/fi';
import { AuthContext } from '../../context/AuthContext';
import AdminServices from './AdminServices';
import AdminPricing from './AdminPricing';
import AdminContacts from './AdminContacts';
import AdminStats from './AdminStats';
import AdminSettings from './AdminSettings';
import AdminAbout from './AdminAbout';
import AdminHomeImages from './AdminHomeImages';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin', label: 'Tableau de bord', icon: FiBarChart2 },
    { path: '/admin/home-images', label: 'Images Accueil', icon: FiImage },
    { path: '/admin/services', label: 'Services', icon: FiFileText },
    { path: '/admin/pricing', label: 'Tarifs', icon: FiDollarSign },
    { path: '/admin/about', label: 'À propos', icon: FiUser },
    { path: '/admin/contacts', label: 'Messages', icon: FiMail },
    { path: '/admin/settings', label: 'Paramètres', icon: FiShield }
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="admin-dashboard">
      <div className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo-wrapper">
            <div className="sidebar-logo">
              <FiShield className="sidebar-logo-icon" />
            </div>
            <h2>VirtualHand</h2>
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>
            <FiX />
          </button>
        </div>
        <nav className="sidebar-nav">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="sidebar-icon-wrapper">
                  <Icon className="sidebar-icon" />
                </div>
                <span className="sidebar-label">{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <motion.button
            onClick={handleLogout}
            className="btn-logout"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiLogOut className="logout-icon" />
            <span>Déconnexion</span>
          </motion.button>
        </div>
      </div>

      <div className="admin-main">
        <header className="admin-header">
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)}>
            <FiMenu />
          </button>
          <h1>Panneau d'administration</h1>
          <motion.a
            href="/"
            className="btn-view-site"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiExternalLink className="btn-view-site-icon" />
            <span>Voir le site</span>
          </motion.a>
        </header>

        {/* Menu horizontal responsive */}
        <nav className="admin-nav-responsive">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-responsive-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                title={item.label}
              >
                <Icon className="nav-responsive-icon" />
                <span className="nav-responsive-label">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="admin-content">
          <Routes>
            <Route path="/" element={<AdminStats />} />
            <Route path="/home-images" element={<AdminHomeImages />} />
            <Route path="/services" element={<AdminServices />} />
            <Route path="/pricing" element={<AdminPricing />} />
            <Route path="/about" element={<AdminAbout />} />
            <Route path="/contacts" element={<AdminContacts />} />
            <Route path="/settings" element={<AdminSettings />} />
          </Routes>
        </div>
      </div>

      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            className="sidebar-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
          ></motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;
